import React, { createContext, useEffect, useState } from 'react'
import { cloneDeep, isEmpty } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { BUNDLE_PRIMARY_PRODUCTS, pillowBundleSkus, pillowSizeMap, PRODUCT_CODES, deliveryTypes } from '@/config/globals'
import { getBundleCatalogData } from '@/services/catalog'
import { useSharedCartService } from '@saatva-bits/pattern-library.modules.cart'
import { isExcludedItem } from '@/utils/product'

const initialState = {}

export const CartTransformerContext = createContext(initialState)

export const CartTransformerProvider = ({ children }) => {
    const sharedCart = useSharedCartService()
    const [catalogBundleData, setCatalogBundleData] = useState([]) // State for bundled product data from the catalog service
    const [bundleRelatedProductCodes, setBundleRelatedProductCodes] = useState({}) // State for bundle productCodes and their respective item relationships
    const [transformedCartItems, setTransformedCartItems] = useState([]) // State for all cart items enriched with Bundle data
    const [completeBundles, setCompleteBundles] = useState([]) // State for all completed bundles in cart
    const [nonBundledCartItems, setNonBundledCartItems] = useState([]) // State for all non bundled items in cart
    const [deliveryGroupings, setDeliveryGroupings] = useState('')

    useEffect(() => {
        const getBundlesCatalogData = async () => {
            const bundleData = await getBundleCatalogData() // Gets all bundle data from the catalog service.
            if (bundleData) {
                const sortedBundleDataByPrice = bundleData.sort((a,b) => b.highestPrice - a.highestPrice )
                setCatalogBundleData(sortedBundleDataByPrice)
                const relatedProductCodes = sortedBundleDataByPrice.reduce((acc, bundle) => {
                    const bundleProducts =  bundle.relationships.find(rel => rel.relationshipLabel === 'bundledProducts')

                    acc[bundle.productCode] = bundleProducts?.relatedProductCodes || []
                    return acc
                }, {})
                setBundleRelatedProductCodes(relatedProductCodes)
            }
        }

        catalogBundleData.length === 0 && getBundlesCatalogData()
    }, [])

    // Logic to apply the cart bundle grouping and single cart items
    useEffect(() => {
        if (sharedCart.cart && sharedCart.cart.items.length > 0 && catalogBundleData) {
            const { completedBundleGroupsInCart, transformedCartItems, nonBundledItemsInCart } = getBundlesFromPrimaryProducts()
            setTransformedCartItems(transformedCartItems)
            setCompleteBundles(completedBundleGroupsInCart)
            setNonBundledCartItems(nonBundledItemsInCart)
        }
    }, [sharedCart.cart, catalogBundleData])

    // Updates delivery groupings in state when cart is updated
    useEffect(() => {
        getDeliveryGroupings()
    }, [nonBundledCartItems])
    
    // Helper functions =========================================================
    /**
     * Gets the corresponding single qty pillow sku for a pillow 2-pack
     * @param {string} sku 2-pack pillow sku
     * @returns {string} respective single qty pillow sku
    */
    const getOriginalPillowBundleSku = (sku) => {
        const values = Object.values(pillowBundleSkus)
        const keys = Object.keys(pillowBundleSkus)
        const index = values.indexOf(sku)
        return keys[index]
    }

    /**
     * Find specific bundle variant from catalog data
     * @param {string} productCode productCode
     * @param {array} itemSkus list of item sku strings
     * @param {string} bundleSku a specific bundle variant sku to match directly
     * @param {string} size a specific size to find the first variant that matches
     * @param {object} pillowItem pillow data that must include at least a sku and quanitity key/value to match 2pack vs 1pack
     * @returns {object} Object containing specific bundle variant matching the bundle sku or found item skus
    */
    const findBundleVariant = (productCode = null, itemSkus = [], bundleSku = null, size = null, pillowItem = {}) => {
        if (!catalogBundleData || catalogBundleData.length < 1 || !productCode) return null
        const catalogData = catalogBundleData.find(bundle => bundle.productCode === productCode)

        if (size) {
            // Finds the first variant that matches the size. If looking for an specific bundle variant use the bundle sku or verify each item sku.
            const normalizedSize = size.toLowerCase().trim()
            return catalogData.variants.find(variant => variant.size.toLowerCase().trim() === normalizedSize)
        } else if (bundleSku) {
            return catalogData.variants.find(variant => variant.sku === bundleSku)
        } else if (!isEmpty(pillowItem)) {
            return catalogData.variants.find(variant => {
                const itemsToSearch = [...itemSkus, pillowItem]
                // Check each item in itemSkus has a matching sku and quantity in the variant
                return itemsToSearch.every(item => {
                    if (typeof item === 'object') {
                        const matchingBundledSku = variant.bundledVariants.find(variant => variant.sku === item.sku)
                        return matchingBundledSku && matchingBundledSku.quantity === item.quantity
                    } else {
                        return variant.bundledSkus.includes(item)
                    }
                })
            })
        } else {
            return catalogData.variants.find(variant => itemSkus.every(sku => variant.bundledSkus.includes(sku)))
        }
    }

    /**
     * This function is meant to enrich each cart item with bundle data and also clone items when needed so that shared bundled items show in their respective bundle group.
     * @param {array} products list of product data to be enriched and/or cloned
     * @param {object} primaryProduct Primary product data
     * @param {object} bundleVariant The matching bundle variant data if applicable
     * @returns {object} Object containing enriched cart items that are bundled and non bundled
    */ 
    const cloneAndEnrichBundleProducts = (products, primaryProduct, bundleVariant = {}) => {
        if (products.length === 0) return {bundledProducts: [], unBundledClones: []}

        const unBundledClones = []
        let clonedPrimaryProduct = {}

        const cloneItem = (product, qty) => {
            const amountToSplitDiscount = product.quantity
            const bundledCloneProduct = cloneDeep(product)
            bundledCloneProduct.quantity = qty
            bundledCloneProduct.originalQuantity = product.originalQuantity || product.quantity
            bundledCloneProduct.uid = uuidv4()
            bundledCloneProduct.isBundled = true
            bundledCloneProduct.bundleProductCode = bundleVariant.productCode || null
            bundledCloneProduct.bundleSku = bundleVariant.sku || null
            // Split discounts evenly across cloned items
            bundledCloneProduct.discounts.forEach((discount) => {
                discount.amount = (discount.amount / amountToSplitDiscount) * qty
            })

            const unBundledCloneProduct = cloneDeep(product)
            const unbundledNewQty = product.quantity - qty
            unBundledCloneProduct.quantity = unbundledNewQty
            unBundledCloneProduct.originalQuantity = product.originalQuantity || product.quantity
            unBundledCloneProduct.uid = uuidv4()
            unBundledCloneProduct.isBundled = false
            unBundledCloneProduct.discounts.forEach((discount) => {
                discount.amount = (discount.amount / amountToSplitDiscount) * (unbundledNewQty)
            })

            return {bundledCloneProduct, unBundledCloneProduct}
        }

        const multipleBundleQty = products.every(product => product.quantity === primaryProduct.quantity || product.quantity > primaryProduct.quantity) // Determines if there are multiple complete bundles. If so, then the primary product should not be cloned.
        const bundledProducts = products.map((product) => {
            if ((product.sku === primaryProduct.sku && primaryProduct.quantity > 1 && !multipleBundleQty)) { // Condition for primary products.
                const {bundledCloneProduct, unBundledCloneProduct} = cloneItem(product, 1)

                unBundledClones.push(unBundledCloneProduct)
                clonedPrimaryProduct = bundledCloneProduct
                return bundledCloneProduct
            } else if (product.quantity > primaryProduct.quantity || product.quantity > clonedPrimaryProduct.quantity) { // Condition for other products
                const quantityToClone = !isEmpty(clonedPrimaryProduct) ? clonedPrimaryProduct.quantity : primaryProduct.quantity
                const {bundledCloneProduct, unBundledCloneProduct} = cloneItem(product, quantityToClone)

                unBundledClones.push(unBundledCloneProduct)
                return bundledCloneProduct
            } else { // condition for items not being cloned
                const transformedProduct = {
                    ...product,
                    isBundled: true,
                    bundleProductCode: bundleVariant.productCode || null,
                    bundleSku: bundleVariant.sku || null,
                    uid: uuidv4()
                }

                return transformedProduct
            }
        })

        return {
            bundledProducts,
            unBundledClones
        }
    }

    /**
     * Separates cart items into pillow and non-pillow items based on size and product relationships
     * @param {Array} cartItems - List of items in the cart
     * @param {Object} product - Primary product data
     * @param {string} primaryProductSize - Size of the primary product
     * @param {Array} relatedBundleProductCodes - List of product codes that are related to the bundle
     * @returns {Object} Object containing separated non-pillow items and potential pillow items
     */
    const separateCartItemsForBundle = (cartItems, product, primaryProductSize, relatedBundleProductCodes) => {
        return cartItems.reduce((acc, item) => {
            if (acc.nonPillowItems.find(accItem => accItem.productCode === item.productCode)) return acc
            const itemSize = item.productAttributes.find(att => att.name === 'Size')?.value || 'oneSize'
            const isPillow = item.subcategory === 'Pillows'

            if (isPillow) {
                const pillowSize = pillowSizeMap[primaryProductSize]
                if (pillowSize === itemSize) {
                    acc.potentialPillowItems.push(item)
                } else {
                    acc.nonRelatedPillows.push(item)
                }
                return acc
            }

            if (item.sku === product.sku || 
                (itemSize === primaryProductSize || itemSize === 'oneSize') && 
                relatedBundleProductCodes.includes(item.productCode) && 
                item.productCode !== product.productCode
            ) {
                acc.nonPillowItems.push(item)
            }

            return acc
        }, { nonPillowItems: [], potentialPillowItems: [], nonRelatedPillows: [] })
    }

    /**
     * Searches for a matching pillow variant in the bundle configuration
     * @param {Array} pillowItems - List of potential pillow items from cart
     * @param {Array} nonPillowSkus - List of non-pillow SKUs to match with
     * @param {string} bundleProductCode - Product code of the bundle being evaluated
     * @returns {Object} Object containing matched pillow item and bundle variant with pillow
     */
    const findMatchingPillowVariant = (pillowItems, nonPillowSkus, bundleProductCode) => {
        if (pillowItems.length === 0) {
            return { matchedPillowItem: null, bundleVariantWithPillow: null, unMatchedPillowItems: []}
        }

        let foundVariant = null
        const matchedPillow = pillowItems.find(pillowItem => {
            const isPillowTwoPack = pillowItem.name.includes('2-Pack Bundle')
            const pillowSku = isPillowTwoPack ? getOriginalPillowBundleSku(pillowItem.sku) : pillowItem.sku
            const pillowData = { sku: pillowSku, quantity: isPillowTwoPack ? 2 : 1 }

            if ([...nonPillowSkus, pillowSku].length === bundleRelatedProductCodes[bundleProductCode].length) {
                foundVariant = findBundleVariant(bundleProductCode, nonPillowSkus, null, null, pillowData)
                return !!foundVariant
            }
            return false
        })

        if (matchedPillow) {
            const unMatchedPillowItems = pillowItems.filter(pillowItem => pillowItem.sku !== matchedPillow.sku)
            return { matchedPillowItem: matchedPillow, bundleVariantWithPillow: foundVariant, unMatchedPillowItems }
        }

        return { matchedPillowItem: null, bundleVariantWithPillow: null, unMatchedPillowItems: pillowItems }
    }

    /**
     * Sorts bundle products to ensure primary product is first in the array
     * @param {Array} products - List of bundle products to sort
     * @param {string} primarySku - SKU of the primary product
     * @returns {Array} Sorted array of products with primary product first
     */
    const sortBundleProducts = (products, primarySku) => {
        return products.sort((a, b) => {
            if (a.sku === primarySku) return -1
            if (b.sku === primarySku) return 1
            return 0
        })
    }

    /**
     * Creates a bundle group object combining variant data with bundled products
     * @param {Object} bundleVariant - Bundle variant data from catalog
     * @param {Array} bundledProducts - List of products in the bundle
     * @returns {Object} Combined bundle group object with variant data and items
     */
    const createBundleGroup = (bundleVariant, bundledProducts) => ({
        ...bundleVariant,
        items: [...bundledProducts]
    })

    /**
     * Updates cart items by filtering out bundled SKUs and adding unbundled clones
     * @param {Array} nonBundledCartItems - List of non-bundled items in cart
     * @param {Array} bundledSkus - List of SKUs that are now part of bundles
     * @param {Array} unBundledClones - List of cloned items that remain unbundled
     * @returns {Array} Updated list of cart items
     */
    const getUpdatedCartItems = (nonBundledCartItems, bundledSkus, unBundledClones) => {
        const filteredItems = nonBundledCartItems.filter(item => !bundledSkus.includes(item.sku))
        return [...filteredItems, ...unBundledClones]
    }

    /**
     * Finds remaining products that could complete a bundle based on product codes
     * @param {Array} bundledItems - Current list of items already identified as part of the bundle
     * @param {string} bundleProductCode - Product code of the bundle being evaluated
     * @param {Object} bundleRelatedProductCodes - Object containing bundle product codes and their related product codes
     * @param {Array} nonBundledCartItems - List of cart items that are not yet part of any bundle
     * @returns {Object} Object containing additional products found and their bundled versions
     */
    const findRemainingBundleProducts = (bundledItems, bundleProductCode, bundleRelatedProductCodes, nonBundledCartItems) => {
        const foundProductCodes = bundledItems.map(item => item.productCode)
        const remainingProductCodes = bundleRelatedProductCodes[bundleProductCode]
            .filter(productCode => !foundProductCodes.includes(productCode))
    
        const additionalProducts = []
        const additionalBundledItems = []
    
        remainingProductCodes.forEach(productCode => {
            const bundleProduct = nonBundledCartItems.find(item => 
                item.productCode === productCode
            )
            
            if (bundleProduct) {
                additionalProducts.push(bundleProduct)
                additionalBundledItems.push(bundleProduct)
            }
        })
    
        return { additionalProducts, additionalBundledItems }
    }
    // End of Helper functions =========================================================

    /**
     * This function is meant to check each primary product for matching bundle variants.
     * A cart could have multiple primary products such as a saatva classic in queen and a saatva classic in full size.
     * It groups products by size of the primary product. If non are found then it moves to the primary product.
     * @param {array} nonBundledCartItems list of un bundled cart items.
     * @param {array} primaryProducts List of all primary products and their respective data.
     * @param {string} bundleProductCode the productCode of the bundle group being evaluated.
     * Returns the specific primary product data, the matched bundle variant, and the matching bundle items from the cart. These are used to make the final grouped bundle. 
    */
    const findBundleVariantFromPrimaryProducts = (nonBundledCartItems = [], primaryProducts = [], bundleProductCode) => {
        // Early return if no primary products
        if (primaryProducts.length === 0) {
            return { primaryProductData: {}, bundleVariant: null, matchingBundleProducts: null }
        }
        let primaryProductData = {}
        let bundleVariant = null
        let matchingBundleProducts = null

        // Find first matching bundle variant among primary products
        
        primaryProducts.forEach((product) => {
            if (bundleVariant) return // If there is a bundleVariant found already skip this iteration of the loop to use the found data.

            const primaryProductSize = product.productAttributes.find(att => att.name ==='Size')?.value
            const relatedBundleProductCodes = bundleRelatedProductCodes[bundleProductCode]
            
            // Check if all required product codes are in cart
            const itemProductCodesToSearch = nonBundledCartItems.map(item => item.productCode)
            const isBundleCompleteInCart = relatedBundleProductCodes.every((productCode) => itemProductCodesToSearch.includes(productCode))
            if (!isBundleCompleteInCart) {
                return
            }

            // Separate items into non-pillows, pillows that could be bundled, and pillows that could not be bundled.
            const { nonPillowItems, potentialPillowItems, nonRelatedPillows } = separateCartItemsForBundle(
                nonBundledCartItems,
                product,
                primaryProductSize,
                relatedBundleProductCodes
            )

            // Try to find bundle variant without pillows first
            const nonPillowItemSkus = nonPillowItems.map(item => item.sku)
            const canBeVariant = nonPillowItemSkus.length === relatedBundleProductCodes.length
            let bundleVariantData = canBeVariant ? findBundleVariant(bundleProductCode, nonPillowItemSkus) : null
            let matchedPillow = null
            const unMatchedPillows = [...nonRelatedPillows]
            

            // If no variant found, try with pillows
            if (!bundleVariantData) {
                const { matchedPillowItem, bundleVariantWithPillow, unMatchedPillowItems } = findMatchingPillowVariant(
                    potentialPillowItems,
                    nonPillowItemSkus,
                    bundleProductCode
                )
                matchedPillow = matchedPillowItem
                unMatchedPillows.push(...unMatchedPillowItems)
                bundleVariantData = bundleVariantWithPillow
            }
            
            const bundleProducts = matchedPillow ? [...nonPillowItems, matchedPillow] : isEmpty(bundleVariantData) ? [...nonPillowItems, ...unMatchedPillows] : nonPillowItems
            if (!isEmpty(bundleVariantData)) {
                primaryProductData = product
                bundleVariant = bundleVariantData
                matchingBundleProducts = sortBundleProducts(bundleProducts, product.sku)
                return
            }

            // Handle case where no variant was found but products are grouped
            if (bundleProducts.length > 1) {
                const isOnlyPrimaryProduct = bundleProducts.length === 1 && bundleProducts[0].productCode === product.productCode
            
                primaryProductData = product
                bundleVariant = null
                matchingBundleProducts = isOnlyPrimaryProduct ? null : 
                    sortBundleProducts(bundleProducts, product.sku)
                return
            }
        })

        return {
            primaryProductData,
            bundleVariant,
            matchingBundleProducts
        }
    }

    /**
     * This function serves to group products into a known bundle variant from the catalog service based on productCode and sku.
     * Evaluates first if the primary product is in cart and then determines which bundle grouping is complete by looking for the related product codes and skus.
     * It recursively loops through all of the cart items for each primary product found to complete a bundle or leave un bundled.
     * @param {array} nonBundledCartItems list of un bundled cart items.
     * @param {string} primaryProductCode the productCode of the primary bundle product.
     * @param {string} bundleProductCode the productCode of the bundle group being evaluated.
     * @param {array} completedBundleGroupsAcc completed bundle groups accumulator
     * @param {array} bundleItemsAcc completed bundle items accumulator
     * Returns all unbundled cart items, the completed bundle groups, and a list of all item in cart.
    */ 
    const evaluateCartItemsForEstablishedBundleVariants = (nonBundledCartItems = [], primaryProductCode, bundleProductCode, completedBundleGroupsAcc = [], bundleItemsAcc = []) => {
        // Early return if no items to process
        if (!nonBundledCartItems.length) {
            return {
                nonBundledCartItems: [],
                bundledItems: bundleItemsAcc,
                completedBundleGroups: completedBundleGroupsAcc
            }
        }

        const primaryProducts = nonBundledCartItems.filter(item => item.productCode === primaryProductCode) // List of primary products for a specific productCode found in cart.
        const { primaryProductData, bundleVariant, matchingBundleProducts } = findBundleVariantFromPrimaryProducts(nonBundledCartItems, primaryProducts, bundleProductCode) // Evaluate each primary product against all non bundled cart items to find a bundle variant match or not.

        // Return if there is no matched bundle variant or any matching bundle products, meaning all possible variants were found and grouped already.
        if (!bundleVariant || !matchingBundleProducts) {
            return {
                nonBundledCartItems,
                bundledItems: bundleItemsAcc,
                completedBundleGroups: completedBundleGroupsAcc
            }
        }

        // If a bundle variant was matched use that data for the bundle grouping.
        const {bundledProducts, unBundledClones} = cloneAndEnrichBundleProducts(matchingBundleProducts, primaryProductData, bundleVariant) // enrich with bundle data and clone item data if required.

        const bundledSkus = bundledProducts.map(item => item.sku)
        const bundleGroup = createBundleGroup(bundleVariant, bundledProducts)
        
        // Prepare updated collections for next iteration
        const updatedCompletedBundles = [...completedBundleGroupsAcc, bundleGroup]
        const updatedBundleItems = [...bundleItemsAcc, ...bundledProducts]
        const remainingCartItems = getUpdatedCartItems(
            nonBundledCartItems, 
            bundledSkus, 
            unBundledClones
        )

        // Recursive call with updated collections
        return evaluateCartItemsForEstablishedBundleVariants(
            remainingCartItems,
            primaryProductCode,
            bundleProductCode,
            updatedCompletedBundles,
            updatedBundleItems
        )
    }

    /**
     * This function serves to group products into any possible bundle based on productCode alone. The bundle data is represented by the first bundle variant found based on size.
     * Evaluates first if the primary product is in cart and then determines which bundle grouping is complete by looking for the related product codes and skus.
     * It recursively loops through all of the cart items for each primary product found to complete a bundle if possible
     * @param {array} nonBundledCartItems list of un bundled cart items.
     * @param {string} primaryProductCode the productCode of the primary bundle product.
     * @param {string} bundleProductCode the productCode of the bundle group being evaluated.
     * @param {array} completedBundleGroupsAcc completed bundle groups accumulator
     * @param {array} bundleItemsAcc completed bundle items accumulator
     * Returns all unbundled cart items, the completed bundle groups (not matching the catalog service data), and a list of all items in cart.
    */ 
    const evaluateCartItemsForAnyBundleGroup = (nonBundledCartItems = [], primaryProductCode, bundleProductCode, completedBundleGroupsAcc = [], bundleItemsAcc = []) => {
        const primaryProducts = nonBundledCartItems.filter(item => item.productCode === primaryProductCode) // List of primary products for a specific productCode found in cart.
        const { primaryProductData, matchingBundleProducts } = findBundleVariantFromPrimaryProducts(nonBundledCartItems, primaryProducts, bundleProductCode) // Evaluate each primary product against all non bundled cart items to find any matching products by size.

        // Determine which products to search
        let isPrimaryProductOnly = (!isEmpty(primaryProductData) && !matchingBundleProducts) && [primaryProductData]
        let productsToSearch = isPrimaryProductOnly ? isPrimaryProductOnly : matchingBundleProducts

        // Early return if no products to search
        if (!productsToSearch) {
            return {
                nonBundledCartItems,
                bundledItems: bundleItemsAcc,
                completedBundleGroups: completedBundleGroupsAcc
            }
        }
       
        // Find all items that are part of the bundle based on their product codes
        const bundledItems = productsToSearch.filter(item => 
            bundleRelatedProductCodes[bundleProductCode].includes(item.productCode)
        )

        // Look for remaining products if bundle is incomplete
        if (bundledItems.length < bundleRelatedProductCodes[bundleProductCode].length) {
            const { additionalProducts, additionalBundledItems } = findRemainingBundleProducts(
                bundledItems,
                bundleProductCode,
                bundleRelatedProductCodes,
                nonBundledCartItems
            )
            
            productsToSearch.push(...additionalProducts)
            bundledItems.push(...additionalBundledItems)
        }

        // Check if bundle is complete
        const isBundleComplete = bundledItems.length === bundleRelatedProductCodes[bundleProductCode].length
        if (!isBundleComplete || isEmpty(primaryProductData)) {
            return {
                nonBundledCartItems,
                bundledItems: bundleItemsAcc,
                completedBundleGroups: completedBundleGroupsAcc
            }
        }

        // If the bundle is complete then we can proceed and process the bundle.
        const {bundledProducts, unBundledClones} = cloneAndEnrichBundleProducts(bundledItems, primaryProductData, {productCode: bundleProductCode})
        const bundledSkus = bundledProducts.map(item => item.sku)
        const primaryProductSize = primaryProductData.productAttributes.find(att => att.name ==='Size')?.value
        
        const bundleMetaData = findBundleVariant(bundleProductCode, null, null, primaryProductSize) // Finds closest bundle variant based on size of primary product for bundle meta data.
        const nonMatchingBundleGroup = createBundleGroup(bundleMetaData, bundledProducts)

        // Prepare updated collections for next iteration
        const updatedCompletedBundles = [...completedBundleGroupsAcc, nonMatchingBundleGroup]
        const updatedBundleItems = [...bundleItemsAcc, ...bundledProducts]
        const remainingCartItems = getUpdatedCartItems(
            nonBundledCartItems, 
            bundledSkus, 
            unBundledClones
        )

        // Recursive call with updated collections
        return evaluateCartItemsForAnyBundleGroup(
            remainingCartItems,
            primaryProductCode,
            bundleProductCode,
            updatedCompletedBundles,
            updatedBundleItems
        )
    }

    // Groups cart items into bundle groups based of the primary product of each bundle.
    const getBundlesFromPrimaryProducts = () => {
        if (!sharedCart.cart || sharedCart.cart.items === 0 || isEmpty(bundleRelatedProductCodes) || !catalogBundleData) return []
        const clonedCartItems = cloneDeep(sharedCart.cart.items).filter((item) => !isExcludedItem(item))

        let nonBundledItemsInCart = clonedCartItems
        let transformedBundleItems = []
        const completedBundleGroupsInCart = []

        /*
            * The following code is used to group cart items into bundles.
            * The first loop will evaluate each primary bundle product and then each respective bundle grouping to find MATCHED bundled variants, meaning they are established variants in the catalog service.
               * If the classic-starter-bundle is being evaluated a second loop occurs to find UN_MATCHED bundled variants prior to moving on to the next primary product for MATCHED bundles.
            * The second loop will evaluate each primary bundle product and then each respective bundle grouping again to find UN_MATCHED bundled variants, meaning that the bundles are not in the catalog service but contain the correct product codes for a grouping.
            * This repeats for all non bundled cart items until all items are grouped or cannot be grouped.
        */

        // First loop through each primary bundle product and then each respective bundle grouping to find MATCHED bundled variants.
        Object.entries(BUNDLE_PRIMARY_PRODUCTS).forEach(([primaryProductCode, bundleProductCodes]) => {
            bundleProductCodes.forEach((bundleProductCode) => {
                const { nonBundledCartItems, bundledItems, completedBundleGroups } = evaluateCartItemsForEstablishedBundleVariants(nonBundledItemsInCart, primaryProductCode, bundleProductCode)

                // Classic starter bundle is the highest priority bundle so we want to check for unmatched bundles before checking matched other bundles.
                if (bundleProductCode === PRODUCT_CODES.classicStarterBundle && nonBundledCartItems.length) {
                    const unmatchedBundles = evaluateCartItemsForAnyBundleGroup(nonBundledCartItems, primaryProductCode, bundleProductCode)

                    nonBundledItemsInCart = unmatchedBundles.nonBundledCartItems
                    transformedBundleItems.push(...bundledItems, ...unmatchedBundles.bundledItems)
                    completedBundleGroupsInCart.push(...completedBundleGroups, ...unmatchedBundles.completedBundleGroups)
                } else {
                    nonBundledItemsInCart = nonBundledCartItems
                    transformedBundleItems.push(...bundledItems)
                    completedBundleGroupsInCart.push(...completedBundleGroups)
                }
            })
        })

        // Second loop through each primary bundle product and then each respective bundle grouping again to find UN_MATCHED bundled variants.
        // If bundle grouping is changed to remove editing individual items, this logic can be removed.
        Object.entries(BUNDLE_PRIMARY_PRODUCTS).forEach(([primaryProductCode, bundleProductCodes]) => {
            bundleProductCodes.forEach((bundleProductCode) => {
                const { nonBundledCartItems, bundledItems, completedBundleGroups } = evaluateCartItemsForAnyBundleGroup(nonBundledItemsInCart, primaryProductCode, bundleProductCode)

                nonBundledItemsInCart = nonBundledCartItems
                transformedBundleItems.push(...bundledItems)
                completedBundleGroupsInCart.push(...completedBundleGroups)
            })
        })

        const transformedCartItems = [...transformedBundleItems, ...nonBundledItemsInCart]

        // Sort all the bundles based on price highest to lowest.
        completedBundleGroupsInCart.sort((a,b) => {
            const bundlePriorityList = catalogBundleData.map(bundle => bundle.productCode)
            return bundlePriorityList.indexOf(a.productCode) - bundlePriorityList.indexOf(b.productCode)
        })

        return {
            completedBundleGroupsInCart,
            transformedCartItems,
            nonBundledItemsInCart
        }
    }

    // Determines types of delivery groupings in cart and updates state only if there is a change
    const getDeliveryGroupings = () => {
        if (!nonBundledCartItems || nonBundledCartItems && nonBundledCartItems.length === 0) return
        const items = nonBundledCartItems
        const hasWhiteGlove = items.some(item => item.deliveryType === deliveryTypes.whiteGlove)
        const hasDirect = items.some(item => item.deliveryType === deliveryTypes.direct)
        const groupings = hasWhiteGlove && hasDirect ? 'Both' : hasWhiteGlove ? 'White Glove Only' : 'Direct Ship Only'

        if (groupings !== deliveryGroupings) {
            setDeliveryGroupings(groupings)
        }
    }

    return (
        <CartTransformerContext.Provider
            value={{
                transformedCartItems,
                completeBundles,
                nonBundledCartItems,
                deliveryGroupings
            }}
        >
            {children}
        </CartTransformerContext.Provider>
    )
}
